import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import FilterWrapperBasic, {filterWrapperBasicAllSelectionsId as allSelectionsId} from '@adv-mt/mt-filter-wrapper-basic'
import Select, {formSelectSizes} from '@adv-mt/mt-form-select'

const DEFAULT_EMPTY_TOTAL_RESULTS = 0
const LEGACY_WRAPPER = 'legacy'

const FormTechnicalModel = ({
  aggregations,
  initialSearch = {},
  isMultiMakeModelVersion = false,
  onChange,
  overlayContentRef,
  pageName = '',
  type,
  value = {},
  vehiclePosition,
  wrapper = LEGACY_WRAPPER
}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')
  const {FILTER_NAMES} = config.get('search')
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const {DEFAULT_VEHICLE_SEARCH_VALUE} = config.get('searchFilters')

  const {
    TECHNICAL_DETAILS_SEARCH_PAGE_NAME,
    TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME,
    MAKES_DISTRIBUTOR_PAGE_NAME,
    MAKES_SIZES_DISTRIBUTOR_PAGE_NAME
  } = config.get('page')
  const {VEHICLE_SPECS_CONTEXT, VEHICLE_SPEC_PARAMS} = config.get('vehicles')
  const [models, setModels] = useState([])
  const {makeId, categoryId} = initialSearch
  const placeholder = i18n.t('COMMON_ALL_MODELS')
  const FIRST_OPTION = {id: 0, label: placeholder}

  const isTechnicalDetailsSearchPage =
    pageName === TECHNICAL_DETAILS_SEARCH_PAGE_NAME || pageName === TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME

  const isMakesDistributorPage =
    pageName === MAKES_DISTRIBUTOR_PAGE_NAME || pageName === MAKES_SIZES_DISTRIBUTOR_PAGE_NAME

  const getExtendedParams = () => {
    if (isTechnicalDetailsSearchPage) return {}
    if (isMakesDistributorPage) return VEHICLE_SPEC_PARAMS.MARKETED_ITEMS
    return VEHICLE_SPEC_PARAMS.MANUAL_ITEMS
  }

  const extendedParams = getExtendedParams()

  const getItemTotalResults = ({itemId}) =>
    aggregations.find(({id}) => id === itemId)?.totalResults || DEFAULT_EMPTY_TOTAL_RESULTS

  const mapSelectOption = ({items}) => {
    const hasAggregations = Boolean(aggregations)

    return items.map(item => {
      return {
        id: item.value,
        label: item.text,
        ...(hasAggregations && {
          extendedInfo: i18n.n(Number(getItemTotalResults({itemId: item.value})))
        })
      }
    })
  }

  useEffect(() => {
    if (!makeId) return

    domain
      .get('get_specs_vehicles_use_case')
      .execute({
        nextField: VEHICLE_SPECS_CONTEXT.MODEL,
        params: {makeId, section1Id: categoryId, ...extendedParams}
      })
      .then(([error, response]) => {
        if (error) return

        const {items} = response
        const parsedItems = mapSelectOption({items})
        setModels(parsedItems)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, makeId])

  const handleChangeLegacy = (e, {value: id}) => {
    const getSelectedModelLabel = () => {
      const selectedModel = models.find(model => model.id === id)
      const hasSelectedModel = Boolean(selectedModel)
      return hasSelectedModel && selectedModel.label
    }

    return onChange(e, {
      field: FILTER_NAMES.MODEL,
      value: id ? {id, name: getSelectedModelLabel({id})} : {},
      lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.MODEL]
    })
  }

  const handleChange = ({value}) => {
    const [id] = value
    handleChangeLegacy(null, {value: Number(id)})
  }

  const handleReset = ({filters}) => {
    const [filterName] = filters
    onChange(null, {field: filterName, value: DEFAULT_VEHICLE_SEARCH_VALUE.modelId})
  }

  if (wrapper === LEGACY_WRAPPER) {
    return (
      <Select
        aggregations={aggregations}
        disabled={!makeId}
        field={FILTER_NAMES.MODEL}
        initialSearch={initialSearch}
        isMultiMakeModelVersion={isMultiMakeModelVersion}
        label={i18n.t('COMMON.PLURALS.MODEL', 1)}
        onChange={handleChangeLegacy}
        options={[FIRST_OPTION, ...models]}
        overlayContentRef={overlayContentRef}
        pageName={pageName}
        placeholder={placeholder}
        size={formSelectSizes.LARGE}
        value={value.id}
        vehiclePosition={vehiclePosition}
      />
    )
  }

  return (
    <FilterWrapperBasic
      disabled={!makeId}
      filters={[
        {
          allSelectionsLabel: i18n.t('COMMON_ALL_MODELS'),
          name: FILTER_NAMES.MODEL,
          options: models,
          type
        }
      ]}
      group={`${FILTER_NAMES.MODEL}Group`}
      label={i18n.t('COMMON.PLURALS.MODEL', 1)}
      onChange={({value}) => {
        handleChange({value: value.filter(id => id !== allSelectionsId)})
      }}
      onReset={handleReset}
      overlayContentRef={overlayContentRef}
      placeholder={value.id ? models.find(({id}) => id === String(value.id))?.label : placeholder}
      value={{[FILTER_NAMES.MODEL]: [value.id || allSelectionsId]}}
      wrapper={wrapper}
    />
  )
}

FormTechnicalModel.displayName = 'FormTechnicalModel'
FormTechnicalModel.propTypes = {
  aggregations: PropTypes.array,
  initialSearch: PropTypes.shape({
    makeId: PropTypes.number
  }),
  isMultiMakeModelVersion: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  overlayContentRef: PropTypes.node,
  pageName: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.number,
  vehiclePosition: PropTypes.string,
  wrapper: PropTypes.string
}

export default FormTechnicalModel
